import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from "antd";
import { useState } from "react";
import dayjs from "dayjs";
import View from "../controls/View";
import InModal from "../controls/InModal";
import { EmployeeUser } from "../repository/data.default";
import { deleteEmployee, getEmployee, updateEmployee } from "../repository/data.repository";
import { GenderType } from "../repository/default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import useUserProfileCard from "../hooks/useUserProfileCard";
import UserProfileActions from "./user-card/UserProfileActions";
import UserActionDialogs from "./user-card/UserActionDialogs";
import UserMetricsPanel from "./user-card/UserMetricsPanel";
import CompanyProfileSection from "./user-card/CompanyProfileSection";
import {
  userIdentityFields,
  userContactFields,
  userAccountFields,
} from "../controls/userProfileFields";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";

export interface EmployeeViewProps {
  readonly open?: boolean;
  readonly mode: "view" | "delete";
  readonly uid?: string;
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

const identityFields: FieldConfig[] = userIdentityFields();

// Employee-specific details (gender + external identifiers) — corporate-membership tier.
const detailFields: FieldConfig[] = [
  {
    key: "gender",
    label: "Gender",
    editable: true,
    input: (
      <Select
        size="small"
        popupMatchSelectWidth={false}
        // Full canonical gender set (matches the registration wizard + grid filter) so imported
        // NonBinary/Unspecified employees display and remain editable.
        options={Object.values(GenderType).map((g) => ({ value: g, label: g }))}
      />
    ),
  },
  {
    key: "internalId",
    label: "Internal ID",
    editable: true,
    input: <InputNumber size="small" className="w-full" />,
  },
  {
    key: "externalId",
    label: "External ID",
    editable: true,
    span: 2,
    input: <Input size="small" />,
  },
];

function EmployeeView(props: EmployeeViewProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const card = useUserProfileCard({
    uid: props.uid,
    open: props.open,
    getFn: getEmployee,
    updateFn: updateEmployee,
    withHistory: true,
  });
  const { data, editor } = card;
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(false);

  const canEdit = permissions.can("CompanyEmployees", "edit");

  function onDelete(): void {
    setDeleting(true);
    setError(false);
    deleteEmployee(props.uid!)
      .then(() => {
        setDeleting(false);
        props.onFinish?.();
      })
      .catch(() => {
        setDeleting(false);
        setError(true);
      });
  }

  const contactFields: FieldConfig[] = userContactFields(data, {
    editable: true,
    email: { onConfirm: canEdit ? () => card.onInputOpen("confirmEmail") : undefined },
    phone: { onConfirm: canEdit ? () => card.onInputOpen("confirmPhone") : undefined },
  });

  return (
    <>
      <View
        open={["view"].includes(props.mode) && props.open}
        loading={card.loading}
        title={"Employee"}
        onBack={() => props.onClose?.()}
        actions={
          <UserProfileActions
            canEdit={canEdit}
            editor={editor}
            item={card.item}
            onInputOpen={card.onInputOpen}
            show={{ assignReward: true }}
          />
        }
      >
        <Form form={editor.form} component={false}>
          <div className="flex flex-col gap-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <Descriptions
                title="Identity"
                bordered
                size="small"
                column={2}
                items={renderFields(identityFields, data, editor.editing)}
              />
              <Descriptions
                title="Contact"
                bordered
                size="small"
                column={1}
                items={renderFields(contactFields, data, editor.editing)}
              />
            </div>

            <CompanyProfileSection
              data={data}
              editing={editor.editing}
              companyPhoneOnConfirm={
                canEdit ? () => card.onInputOpen("confirmCompanyPhone") : undefined
              }
              extraFields={detailFields}
            />

            <Descriptions
              title="Account"
              bordered
              size="small"
              column={2}
              items={[
                ...renderFields(userAccountFields({ joinedDate: false, editable: true }), data, editor.editing),
                { key: "lastLoginAt", label: "Last Login", children: data?.lastLoginAt ? dayjs(data.lastLoginAt).format("DD/MM/YYYY HH:mm") : undefined },
                { key: "loyaltyPoints", label: "Loyalty Points", children: data?.loyaltyPoints },
                  // PD-01 4.3 asks for a "reset status" beside the activation fields. The honest answer is
                  // WHEN a reset was last sent: whether the user then used the link is not knowable here,
                  // because consuming the token changes the password rather than setting a flag. Empty
                  // means none has ever been sent.
                  { key: "passwordResetRequestedAt", label: "Password Reset Sent", children: data?.passwordResetRequestedAt ? dayjs(data.passwordResetRequestedAt).format("DD/MM/YYYY HH:mm") : undefined },
              ]}
            />

            <UserMetricsPanel history={card.history} loyaltyPoints={data?.loyaltyPoints} lastLoginAt={data?.lastLoginAt} createdOn={data?.createdOn} />
          </div>
        </Form>
      </View>

      <InModal
        open={["delete"].includes(props.mode) && props.open}
        size="small"
        title={"Delete Employee"}
        onCancel={() => props.onClose?.()}
        actions={
          <Space className="flex flex-row">
            <Button type={"primary"} danger loading={deleting} onClick={onDelete}>
              Delete
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph>
          <Typography.Text>
            Continue deleting the{" "}
            <Typography.Text className="font-bold">{(card.item as EmployeeUser | null)?.userName}</Typography.Text>{" "}
            employee?
          </Typography.Text>
        </Typography.Paragraph>
        {error && (
          <Alert className="mb-2" message="Something went wrong, try again" type="error" />
        )}
      </InModal>

      <UserActionDialogs
        item={card.item}
        inputMode={card.inputMode}
        inputOpen={card.inputOpen}
        onClose={card.onInputClose}
        onFinish={card.onInputFinish}
      />
    </>
  );
}

export default EmployeeView;
